import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useAuth } from "context/AuthContext";
import { get, post } from "lib/api";
import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import { useCurrency } from "context/CurrencyContext";
import { isCustomerRole, isDistributorRole } from "lib/portalRoles";
import { countryForCompany } from "lib/companyProfile";
import { usePlaidLink } from "react-plaid-link";

export default function CustomerOrderDetail() {
  const { token, pluginRole, authLoading, activeCompany, storefrontTenant } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { formatUSDForDisplay } = useCurrency();

  const role = String(pluginRole || "").toLowerCase();
  const canViewOrder = isCustomerRole(role) || isDistributorRole(role);

  const documentId = String(params.documentId || "").trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const [invoiceArchive, setInvoiceArchive] = useState(null);
  const [payMessage, setPayMessage] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMessage, setCancelMessage] = useState(null);
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);
  const openedPlaidTokenRef = useRef(null);

  const loadOrder = useCallback(async () => {
    if (!token || !canViewOrder || !documentId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await get(`/api/storefront/orders/${documentId}`);
      setOrder(res?.data || null);
      setInvoiceArchive(res?.invoiceArchive || null);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.message || "Failed to load order";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token, canViewOrder, documentId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const plaidLink = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: async (publicToken, metadata) => {
      setPayLoading(true);
      setPayMessage(null);
      try {
        const accountId =
          metadata?.account_id || metadata?.accounts?.[0]?.id || null;
        await post("/api/storefront/plaid-transfer/pay", {
          data: {
            salesOrderDocumentId: documentId,
            publicToken,
            accountId,
            metadata,
          },
        });
        setPlaidLinkToken(null);
        openedPlaidTokenRef.current = null;
        setPayMessage({
          type: "success",
          text: "Pay-by-bank payment is processing. The invoice will update after funds settle.",
        });
        await loadOrder();
      } catch (e) {
        const msg =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to create Plaid Transfer payment";
        setPayMessage({ type: "error", text: msg });
      } finally {
        setPayLoading(false);
      }
    },
    onExit: () => {
      setPlaidLinkToken(null);
      openedPlaidTokenRef.current = null;
    },
  });

  useEffect(() => {
    if (!plaidLinkToken || !plaidLink.ready || openedPlaidTokenRef.current === plaidLinkToken) return;
    openedPlaidTokenRef.current = plaidLinkToken;
    plaidLink.open();
  }, [plaidLinkToken, plaidLink]);

  const startPlaidPayment = async () => {
    setPayLoading(true);
    setPayMessage(null);
    try {
      const res = await post("/api/storefront/plaid-transfer/link-token", {});
      const linkToken = res?.data?.linkToken;
      if (!linkToken) throw new Error("Plaid did not return a Link token.");
      setPlaidLinkToken(linkToken);
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.message ||
        "Failed to start Plaid payment";
      setPayMessage({ type: "error", text: msg });
    } finally {
      setPayLoading(false);
    }
  };

  const cancelUnpaidBankTransfer = async () => {
    if (!canCancelUnpaidBankTransfer) return;
    if (
      !window.confirm(
        "Cancel this unpaid bank transfer order? Reserved inventory will be released.",
      )
    ) {
      return;
    }
    setCancelLoading(true);
    setCancelMessage(null);
    try {
      await post(`/api/storefront/orders/${documentId}/cancel-unpaid-bank-transfer`, {});
      setCancelMessage({
        type: "success",
        text: "Order cancelled. You can place a new order anytime.",
      });
      await loadOrder();
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.message ||
        "Failed to cancel order";
      setCancelMessage({ type: "error", text: msg });
    } finally {
      setCancelLoading(false);
    }
  };

  if (!token) return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  if (authLoading || pluginRole == null) return null;
  if (!canViewOrder) return <Navigate to="/dashboard" replace />;
  if (!documentId) return <Navigate to="/account/orders" replace />;

  const items = Array.isArray(order?.items) ? order.items : [];
  const total = order?.total != null ? formatUSDForDisplay(Number(order.total)) : "—";
  const shipping = order?.shippingAmount != null ? formatUSDForDisplay(Number(order.shippingAmount)) : "—";
  const subtotal = order?.subTotal != null ? formatUSDForDisplay(Number(order.subTotal)) : null;
  const paymentMethodLabel = String(order?.paymentCollectionMethod || order?.paymentProvider || "—")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
  const isMx = countryForCompany(storefrontTenant || activeCompany) === "MX";
  const paymentStatus = String(order?.paymentStatus || "").toLowerCase();
  const orderStatus = String(order?.status || "").toLowerCase();
  const isBankTransfer =
    String(order?.paymentCollectionMethod || order?.paymentProvider || "")
      .trim()
      .toLowerCase() === "bank_transfer";
  const canCancelUnpaidBankTransfer =
    !!order &&
    isBankTransfer &&
    !["paid", "processing", "refunded"].includes(paymentStatus) &&
    orderStatus !== "cancelled" &&
    orderStatus !== "closed" &&
    String(order?.documentType || "sales_order").toLowerCase() === "sales_order";
  const canPayByBank =
    !!order &&
    !isMx &&
    !isBankTransfer &&
    !["paid", "processing"].includes(paymentStatus) &&
    Number(order?.total || 0) > 0;

  return (
    <CustomerPortalLayout
      title={`Order ${order?.salesOrderNumber || documentId}`}
      subtitle="Order details, tracking, and invoice downloads."
      actions={
        <MDButton variant="outlined" color="info" onClick={() => navigate("/account/orders")}>
          Back to orders
        </MDButton>
      }
    >

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
        {payMessage ? (
          <Alert severity={payMessage.type} sx={{ mt: 2 }}>
            {payMessage.text}
          </Alert>
        ) : null}
        {cancelMessage ? (
          <Alert severity={cancelMessage.type} sx={{ mt: 2 }}>
            {cancelMessage.text}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <CircularProgress size={18} />
            <MDTypography variant="button">Loading…</MDTypography>
          </Box>
        ) : null}

        {order ? (
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={7}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <MDTypography variant="h6" fontWeight={900} gutterBottom>
                  Summary
                </MDTypography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  <Chip size="small" label={`Status: ${order?.status || "—"}`} variant="outlined" />
                  <Chip size="small" label={`Payment: ${order?.paymentStatus || "—"}`} variant="outlined" />
                  <Chip size="small" label={`Method: ${paymentMethodLabel}`} variant="outlined" />
                  <Chip size="small" label={`Total: ${total}`} />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  <Box>
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                      Shipping service
                    </MDTypography>
                    <MDTypography variant="button" fontWeight={700}>
                      {order?.shippingService || "—"}
                    </MDTypography>
                  </Box>
                  <Box>
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                      Shipping amount
                    </MDTypography>
                    <MDTypography variant="button" fontWeight={700}>
                      {shipping}
                    </MDTypography>
                  </Box>
                  <Box>
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                      Tracking number
                    </MDTypography>
                    <MDTypography variant="button" fontWeight={700}>
                      {order?.trackingNumber || "—"}
                    </MDTypography>
                  </Box>
                  <Box>
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                      Estimated ship window
                    </MDTypography>
                    <MDTypography variant="button" fontWeight={700}>
                      {order?.estimatedShipWindow || "—"}
                    </MDTypography>
                  </Box>
                </Box>

                {subtotal ? (
                  <MDTypography variant="caption" color="text" sx={{ opacity: 0.7, display: "block", mt: 1.5 }}>
                    Subtotal: {subtotal}
                  </MDTypography>
                ) : null}
                {order?.bankTransferInstructionsSnapshot ? (
                  <Alert severity="info" sx={{ mt: 2, whiteSpace: "pre-wrap" }}>
                    {order.bankTransferInstructionsSnapshot}
                  </Alert>
                ) : null}
                {canCancelUnpaidBankTransfer ? (
                  <Box sx={{ mt: 2 }}>
                    <MDButton
                      variant="outlined"
                      color="error"
                      disabled={cancelLoading}
                      startIcon={
                        cancelLoading ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <Icon>cancel</Icon>
                        )
                      }
                      onClick={cancelUnpaidBankTransfer}
                    >
                      Cancel unpaid order
                    </MDButton>
                  </Box>
                ) : null}
                {canPayByBank ? (
                  <Box sx={{ mt: 2 }}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      disabled={payLoading}
                      startIcon={payLoading ? <CircularProgress size={14} color="inherit" /> : <Icon>account_balance</Icon>}
                      onClick={startPlaidPayment}
                    >
                      {payLoading ? "Starting..." : "Pay by bank account"}
                    </MDButton>
                  </Box>
                ) : null}
              </Card>

              <Card variant="outlined" sx={{ p: 2, mt: 2, overflowX: "auto" }}>
                <MDTypography variant="h6" fontWeight={900} gutterBottom>
                  Items
                </MDTypography>

                {!items.length ? (
                  <Alert severity="info">No items found.</Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Core</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Line</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((it) => {
                        const inv = it?.inventoryItem || null;
                        const key = it?.documentId || it?.id || `${inv?.documentId || ""}-${inv?.sku || ""}`;
                        const unit = it?.unitPrice != null ? formatUSDForDisplay(Number(it.unitPrice)) : "—";
                        const core = it?.coreCharge != null ? formatUSDForDisplay(Number(it.coreCharge)) : "—";
                        const line = it?.lineTotal != null ? formatUSDForDisplay(Number(it.lineTotal)) : "—";
                        return (
                          <TableRow key={String(key)} hover>
                            <TableCell>
                              <MDTypography variant="button" fontWeight={800}>
                                {inv?.sku ? `${inv.sku} — ` : ""}
                                {inv?.name || it?.notes || "Item"}
                              </MDTypography>
                            </TableCell>
                            <TableCell>{it?.quantity ?? "—"}</TableCell>
                            <TableCell>{unit}</TableCell>
                            <TableCell>{core}</TableCell>
                            <TableCell>{line}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <MDTypography variant="h6" fontWeight={900} gutterBottom>
                  Invoice
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ opacity: 0.75 }}>
                  {invoiceArchive?.mxCfdiUuid
                    ? `CFDI UUID: ${invoiceArchive.mxCfdiUuid}`
                    : "Invoice downloads appear here once available."}
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ opacity: 0.75, mt: 1 }}>
                  Status: {invoiceArchive?.mxCfdiStatus || "pending"}
                </MDTypography>

                {(invoiceArchive?.documentId || invoiceArchive?.id) ? (
                  <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <MDButton
                      variant="outlined"
                      color="info"
                      startIcon={<Icon>picture_as_pdf</Icon>}
                      onClick={() => window.open(`/api/storefront/invoices/${invoiceArchive.documentId || invoiceArchive.id}/cfdi/pdf`, "_blank")}
                    >
                      PDF
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="info"
                      startIcon={<Icon>description</Icon>}
                      onClick={() => window.open(`/api/storefront/invoices/${invoiceArchive.documentId || invoiceArchive.id}/cfdi/xml`, "_blank")}
                    >
                      XML
                    </MDButton>
                  </Box>
                ) : null}
              </Card>

              <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
                <MDTypography variant="h6" fontWeight={900} gutterBottom>
                  Need help?
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ opacity: 0.75, mb: 2 }}>
                  Contact us with your order number and we’ll take care of you.
                </MDTypography>
                <MDButton variant="outlined" color="info" onClick={() => navigate("/catalog")}>
                  Browse catalog
                </MDButton>
              </Card>
            </Grid>
          </Grid>
        ) : null}
    </CustomerPortalLayout>
  );
}
